import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Assignment as EpicIcon,
  Warning as RiskIcon,
  Timeline as RoadmapIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Engineering as RTEIcon,
  CheckCircle as CheckCircleIcon,
  Groups as GroupsIcon,
  TrendingUp as TrendingUpIcon,
  Block as BlockIcon,
  Forum as ForumIcon,
  School as SchoolIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  LocalShipping as TradeIcon,
  Cloud as CloudIcon,
  Computer as ComputerIcon,
  Psychology as AIIcon,
  Radar as RadarIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getAllPIPlans,
  getPIPlanById,
  getPIPlanEpics,
  getPIPlanStories,
  updateEpic,
  updateStory,
  PIPlan,
  Epic,
  Story,
  Risk
} from '../services/piPlanningService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pi-planning-tabpanel-${index}`}
      aria-labelledby={`pi-planning-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const PIPlanningBoard: React.FC = () => {
  const navigate = useNavigate();
  const [quarterTabValue, setQuarterTabValue] = useState(0); // Q1, Q2, Q3, Q4
  const [detailTabValue, setDetailTabValue] = useState(0); // Sprint, Epics, Risks, Roadmap
  const [piPlans, setPiPlans] = useState<PIPlan[]>([]);
  const [selectedPIPlan, setSelectedPIPlan] = useState<PIPlan | null>(null);
  const [epics, setEpics] = useState<Epic[]>([]);
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Edit state management for Sprint Schedule
  const [editingCell, setEditingCell] = useState<{ row: number; field: string } | null>(null);
  const [editValue, setEditValue] = useState<any>('');
  
  // Edit state management for Epics - Form-level editing
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEpic, setSelectedEpic] = useState<Epic | null>(null);
  const [epicFormData, setEpicFormData] = useState<Partial<Epic>>({});
  
  // Edit state management for Stories - Form-level editing
  const [storyDialogOpen, setStoryDialogOpen] = useState(false);
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [storyFormData, setStoryFormData] = useState<Partial<Story>>({});
  
  // Epic expansion state for showing stories
  const [expandedEpics, setExpandedEpics] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadPIPlans();
  }, []);

  useEffect(() => {
    if (selectedPIPlan) {
      loadPIPlanDetails(selectedPIPlan._id);
    }
  }, [selectedPIPlan]);

  const loadPIPlans = async () => {
    try {
      setLoading(true);
      const data = await getAllPIPlans();
      setPiPlans(data);
      if (data.length > 0) {
        // Set the first PI Plan and sync the quarter tab
        const firstPlan = data[0];
        setSelectedPIPlan(firstPlan);
        // Set quarter tab to match the first plan's quarter (quarter 1-4 maps to tab 0-3)
        if (firstPlan.quarter >= 1 && firstPlan.quarter <= 4) {
          setQuarterTabValue(firstPlan.quarter - 1);
        }
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load PI Plans');
    } finally {
      setLoading(false);
    }
  };

  const loadPIPlanDetails = async (piPlanId: string) => {
    try {
      const [epicsData, storiesData] = await Promise.all([
        getPIPlanEpics(piPlanId),
        getPIPlanStories(piPlanId)
      ]);
      setEpics(epicsData);
      setStories(storiesData);
    } catch (err: any) {
      console.error('Error loading PI Plan details:', err);
    }
  };

  const handleQuarterTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setQuarterTabValue(newValue);
    // Load PI Plan for selected quarter (Q1=1, Q2=2, Q3=3, Q4=4)
    const quarter = newValue + 1;
    const quarterPlan = piPlans.find(plan => plan.quarter === quarter && plan.fiscalYear === '26');
    if (quarterPlan) {
      setSelectedPIPlan(quarterPlan);
    } else {
      // If no plan exists for this quarter, try to find any plan for this quarter
      const anyQuarterPlan = piPlans.find(plan => plan.quarter === quarter);
      if (anyQuarterPlan) {
        setSelectedPIPlan(anyQuarterPlan);
      } else {
        console.warn(`No PI Plan found for Q${quarter}`);
        // Optionally show a message to the user
      }
    }
  };

  const handleDetailTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setDetailTabValue(newValue);
  };

  // Click-to-edit handlers
  const handleCellClick = (rowIndex: number, field: string, currentValue: any) => {
    setEditingCell({ row: rowIndex, field });
    setEditValue(currentValue);
  };

  const handleCellBlur = async () => {
    if (editingCell && selectedPIPlan) {
      // Update the sprint data
      const updatedSprints = [...selectedPIPlan.sprints];
      const sprint = updatedSprints[editingCell.row];
      
      if (editingCell.field === 'status') {
        sprint.status = editValue;
      } else if (editingCell.field === 'goals') {
        sprint.goals = editValue.split('\n').filter((g: string) => g.trim());
      }
      
      // Update local state
      setSelectedPIPlan({
        ...selectedPIPlan,
        sprints: updatedSprints
      });
      
      // TODO: Call API to persist changes
      // await updatePIPlan(selectedPIPlan._id, { sprints: updatedSprints });
    }
    setEditingCell(null);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCellBlur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  };

  // Epic form-level editing handlers
  const handleEpicRowClick = (epic: Epic) => {
    console.log('Epic row clicked:', epic.epicId);
    console.log('Epic title:', epic.title);
    console.log('Epic description:', epic.description);
    
    const formData = {
      title: epic.title,
      description: epic.description,
      epicType: epic.epicType,
      owner: epic.owner,
      status: epic.status,
      priority: epic.priority,
      businessValue: epic.businessValue,
      timeCriticality: epic.timeCriticality,
      riskReduction: epic.riskReduction,
      wsjfScore: epic.wsjfScore,
      estimatedEffort: epic.estimatedEffort
    };
    
    console.log('Form data being set:', formData);
    setSelectedEpic(epic);
    setEpicFormData(formData);
    setEditDialogOpen(true);
    console.log('Dialog should be open now');
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setSelectedEpic(null);
    setEpicFormData({});
  };

  const handleFormChange = (field: string, value: any) => {
    setEpicFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEpic = async () => {
    if (!selectedEpic) {
      console.error('No epic selected');
      alert('No epic selected to save');
      return;
    }

    try {
      console.log('Saving epic:', selectedEpic._id, epicFormData);
      
      // Update epic in database
      const updatedEpic = await updateEpic(selectedEpic._id, epicFormData);
      console.log('Epic updated successfully:', updatedEpic);
      
      // Update local state with the response from the server
      setEpics(epics.map(e => 
        e._id === selectedEpic._id ? { ...e, ...updatedEpic } : e
      ));
      
      handleCloseDialog();
      
      // Show success message
      console.log('Epic saved successfully!');
    } catch (error: any) {
      console.error('Error updating epic:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      alert('Failed to save epic: ' + errorMessage);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
      case 'Done':
      case 'Resolved':
        return 'success';
      case 'In Progress':
      case 'Implementing':
        return 'primary';
      case 'At Risk':
      case 'Blocked':
        return 'error';
      case 'Not Started':
      case 'Backlog':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical':
        return 'error';
      case 'High':
        return 'warning';
      case 'Medium':
        return 'info';
      case 'Low':
        return 'default';
      default:
        return 'default';
    }
  };

  // 508-compliant Epic Type colors with proper contrast
  const getEpicTypeColor = (epicType: string) => {
    switch (epicType) {
      case 'Business':
        return { bgcolor: '#0066CC', color: '#FFFFFF' }; // Blue - 4.5:1 contrast
      case 'Enabler':
        return { bgcolor: '#7B68EE', color: '#FFFFFF' }; // Purple - 4.5:1 contrast
      case 'Architectural':
        return { bgcolor: '#2E8B57', color: '#FFFFFF' }; // Green - 4.5:1 contrast
      default:
        return { bgcolor: '#666666', color: '#FFFFFF' };
    }
  };

  // 508-compliant Epic Status colors with proper contrast
  const getEpicStatusColor = (status: string) => {
    switch (status) {
      case 'Done':
        return { bgcolor: '#2E7D32', color: '#FFFFFF' }; // Dark green - 4.5:1 contrast
      case 'Implementing':
        return { bgcolor: '#1976D2', color: '#FFFFFF' }; // Blue - 4.5:1 contrast
      case 'Analyzing':
        return { bgcolor: '#F57C00', color: '#FFFFFF' }; // Orange - 4.5:1 contrast
      case 'Portfolio Review':
        return { bgcolor: '#7B1FA2', color: '#FFFFFF' }; // Purple - 4.5:1 contrast
      case 'Backlog':
        return { bgcolor: '#616161', color: '#FFFFFF' }; // Gray - 4.5:1 contrast
      case 'Cancelled':
        return { bgcolor: '#D32F2F', color: '#FFFFFF' }; // Red - 4.5:1 contrast
      default:
        return { bgcolor: '#757575', color: '#FFFFFF' };
    }
  };

  // Toggle epic expansion to show/hide stories
  const toggleEpicExpansion = (epicId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click from firing
    setExpandedEpics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(epicId)) {
        newSet.delete(epicId);
      } else {
        newSet.add(epicId);
      }
      return newSet;
    });
  };

  // Story form-level editing handlers
  const handleStoryRowClick = (story: Story, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent epic row click
    console.log('Story row clicked:', story.storyId);
    setSelectedStory(story);
    setStoryFormData({
      title: story.title,
      description: story.description,
      assignedTeam: story.assignedTeam,
      status: story.status,
      priority: story.priority,
      storyPoints: story.storyPoints,
      acceptanceCriteria: story.acceptanceCriteria,
      tags: story.tags
    });
    setStoryDialogOpen(true);
    console.log('Story dialog should be open now');
  };

  const handleCloseStoryDialog = () => {
    setStoryDialogOpen(false);
    setSelectedStory(null);
    setStoryFormData({});
  };

  const handleStoryFormChange = (field: string, value: any) => {
    setStoryFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveStory = async () => {
    if (!selectedStory) {
      console.error('No story selected');
      alert('No story selected to save');
      return;
    }

    try {
      console.log('Saving story:', selectedStory._id, storyFormData);
      
      // Update story in database
      const updatedStory = await updateStory(selectedStory._id, storyFormData);
      console.log('Story updated successfully:', updatedStory);
      
      // Update local state with the response from the server
      setStories(stories.map(s => 
        s._id === selectedStory._id ? { ...s, ...updatedStory } : s
      ));
      
      handleCloseStoryDialog();
      
      // Show success message
      console.log('Story saved successfully!');
    } catch (error: any) {
      console.error('Error updating story:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      alert('Failed to save story: ' + errorMessage);
    }
  };

  const getROAMColor = (roamCategory: string) => {
    switch (roamCategory) {
      case 'Resolved':
        return 'success';
      case 'Owned':
        return 'primary';
      case 'Accepted':
        return 'warning';
      case 'Mitigated':
        return 'info';
      default:
        return 'default';
    }
  };

  // Section 508 compliant team color coding with 4.5:1 contrast ratio
  const getTeamColor = (teamName: string) => {
    // Generate consistent color based on team name hash
    const hash = teamName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colors = [
      { bg: '#003366', text: '#FFFFFF' }, // USCIS Blue - Dark
      { bg: '#0066CC', text: '#FFFFFF' }, // Blue - Medium
      { bg: '#2E7D32', text: '#FFFFFF' }, // Green - Dark
      { bg: '#D84315', text: '#FFFFFF' }, // Red-Orange - Dark
      { bg: '#6A1B9A', text: '#FFFFFF' }, // Purple - Dark
      { bg: '#00695C', text: '#FFFFFF' }, // Teal - Dark
      { bg: '#C62828', text: '#FFFFFF' }, // Red - Dark
      { bg: '#AD1457', text: '#FFFFFF' }, // Pink - Dark
      { bg: '#4A148C', text: '#FFFFFF' }, // Deep Purple - Dark
      { bg: '#01579B', text: '#FFFFFF' }, // Light Blue - Dark
    ];
    return colors[hash % colors.length];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateProgress = () => {
    if (!selectedPIPlan) return 0;
    const total = selectedPIPlan.objectives.length;
    const completed = selectedPIPlan.objectives.filter(obj => obj.status === 'Completed').length;
    return total > 0 ? (completed / total) * 100 : 0;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading PI Planning Board...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!selectedPIPlan) {
    const currentQuarter = quarterTabValue + 1;
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            SAFe PI Planning Board - FY26
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            CBP Fiscal Year 2026 Program Increment Planning and Execution
          </Typography>
        </Box>
        
        {/* Quarterly Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={quarterTabValue}
            onChange={handleQuarterTabChange}
            aria-label="FY26 Quarterly tabs"
            variant="fullWidth"
            sx={{
              borderBottom: 2,
              borderColor: 'primary.main',
              '& .MuiTab-root': { 
                textTransform: 'none', 
                fontWeight: 700,
                fontSize: '1rem',
                minHeight: 64,
                color: '#666',
                '&.Mui-selected': {
                  color: 'primary.main',
                  bgcolor: '#f0f4f8'
                }
              },
              '& .MuiTabs-indicator': {
                height: 4,
                bgcolor: 'primary.main'
              }
            }}
          >
            <Tab label="Q1 FY26 (Oct - Dec 2025)" />
            <Tab label="Q2 FY26 (Jan - Mar 2026)" />
            <Tab label="Q3 FY26 (Apr - Jun 2026)" />
            <Tab label="Q4 FY26 (Jul - Sep 2026)" />
          </Tabs>
        </Paper>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          No PI Plan found for Q{currentQuarter} FY26. Create a PI Plan to get started.
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
        >
          Create Q{currentQuarter} PI Plan
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            SAFe PI Planning Board - FY26
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            CBP Fiscal Year 2026 Program Increment Planning and Execution
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={loadPIPlans} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ ml: 1, bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            New PI Plan
          </Button>
        </Box>
      </Box>

      {/* Quarterly Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={quarterTabValue}
          onChange={handleQuarterTabChange}
          aria-label="FY26 Quarterly tabs"
          variant="fullWidth"
          sx={{
            borderBottom: 2,
            borderColor: 'primary.main',
            '& .MuiTab-root': { 
              textTransform: 'none', 
              fontWeight: 700,
              fontSize: '1rem',
              minHeight: 64,
              color: '#666',
              '&.Mui-selected': {
                color: 'primary.main',
                bgcolor: '#f0f4f8'
              }
            },
            '& .MuiTabs-indicator': {
              height: 4,
              bgcolor: 'primary.main'
            }
          }}
        >
          <Tab label="Q1 FY26 (Oct - Dec 2025)" />
          <Tab label="Q2 FY26 (Jan - Mar 2026)" />
          <Tab label="Q3 FY26 (Apr - Jun 2026)" />
          <Tab label="Q4 FY26 (Jul - Sep 2026)" />
        </Tabs>
      </Paper>

      {/* PI Plan Summary Card */}
      <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1 }}>
                {selectedPIPlan.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip label={`FY${selectedPIPlan.fiscalYear}`} color="primary" size="small" />
                <Chip label={`Q${selectedPIPlan.quarter}`} color="primary" size="small" />
                <Chip label={`PI ${selectedPIPlan.piNumber}`} color="primary" size="small" />
                <Chip 
                  label={selectedPIPlan.status} 
                  color={getStatusColor(selectedPIPlan.status) as any} 
                  size="small" 
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                <strong>Duration:</strong> {formatDate(selectedPIPlan.startDate)} - {formatDate(selectedPIPlan.endDate)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Teams:</strong> {selectedPIPlan.teams.map(t => t.teamName).join(', ')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                PI Progress
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={calculateProgress()} 
                sx={{ height: 10, borderRadius: 5, mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {selectedPIPlan.objectives.filter(o => o.status === 'Completed').length} of {selectedPIPlan.objectives.length} objectives completed
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Planned Velocity</Typography>
                  <Typography variant="h6" sx={{ color: 'primary.main' }}>
                    {selectedPIPlan.metrics.plannedVelocity}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Actual Velocity</Typography>
                  <Typography variant="h6" sx={{ color: 'primary.main' }}>
                    {selectedPIPlan.metrics.actualVelocity}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Detail Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={detailTabValue}
          onChange={handleDetailTabChange}
          aria-label="PI Planning detail tabs"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: '#fafafa',
            '& .MuiTab-root': { 
              textTransform: 'none', 
              fontWeight: 600,
              '&.Mui-selected': {
                bgcolor: '#ffffff'
              }
            }
          }}
        >
          <Tab icon={<CalendarIcon />} iconPosition="start" label="Sprint Schedule" />
          <Tab icon={<EpicIcon />} iconPosition="start" label="Epics & Stories" />
          <Tab icon={<RiskIcon />} iconPosition="start" label="ROAM Risks" />
          <Tab icon={<RoadmapIcon />} iconPosition="start" label="Roadmap & Backlog" />
          <Tab icon={<RTEIcon />} iconPosition="start" label="RTE Role" />
          <Tab icon={<SecurityIcon />} iconPosition="start" label="CBP Systems" />
        </Tabs>

        {/* Tab 1: Sprint Schedule */}
        <TabPanel value={detailTabValue} index={0}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Sprint Schedule - {selectedPIPlan.sprints.length} Two-Week Sprints
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Sprint</strong></TableCell>
                  <TableCell><strong>Start Date</strong></TableCell>
                  <TableCell><strong>End Date</strong></TableCell>
                  <TableCell><strong>Goals</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedPIPlan.sprints.map((sprint, sprintIndex) => (
                  <TableRow 
                    key={sprint.sprintNumber} 
                    hover
                    sx={{
                      '& .MuiTableCell-root': {
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: '#f0f4f8'
                        }
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Sprint {sprint.sprintNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(sprint.startDate)}</TableCell>
                    <TableCell>{formatDate(sprint.endDate)}</TableCell>
                    <TableCell
                      onClick={() => handleCellClick(sprintIndex, 'goals', sprint.goals.join('\n'))}
                      role="button"
                      tabIndex={0}
                      aria-label={`Click to edit goals for Sprint ${sprint.sprintNumber}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCellClick(sprintIndex, 'goals', sprint.goals.join('\n'));
                        }
                      }}
                    >
                      {editingCell?.row === sprintIndex && editingCell?.field === 'goals' ? (
                        <TextField
                          multiline
                          rows={3}
                          fullWidth
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={handleKeyDown}
                          autoFocus
                          placeholder="Enter goals (one per line)"
                          variant="outlined"
                          size="small"
                          sx={{ bgcolor: 'white' }}
                          aria-label="Edit sprint goals"
                        />
                      ) : (
                        <Box>
                          {sprint.goals.map((goal, idx) => (
                            <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                              • {goal}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell
                      onClick={() => handleCellClick(sprintIndex, 'status', sprint.status)}
                      role="button"
                      tabIndex={0}
                      aria-label={`Click to edit status for Sprint ${sprint.sprintNumber}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCellClick(sprintIndex, 'status', sprint.status);
                        }
                      }}
                    >
                      {editingCell?.row === sprintIndex && editingCell?.field === 'status' ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handleCellBlur}
                            autoFocus
                            sx={{ bgcolor: 'white' }}
                            aria-label="Edit sprint status"
                          >
                            <MenuItem value="Not Started">Not Started</MenuItem>
                            <MenuItem value="In Progress">In Progress</MenuItem>
                            <MenuItem value="Completed">Completed</MenuItem>
                            <MenuItem value="Blocked">Blocked</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Chip 
                          label={sprint.status} 
                          color={getStatusColor(sprint.status) as any} 
                          size="small" 
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Team Capacity */}
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4 }}>
            Team Capacity
          </Typography>
          <Grid container spacing={2}>
            {selectedPIPlan.teams.map((team) => (
              <Grid item xs={12} md={4} key={team.teamId}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {team.teamName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Members:</strong> {team.members}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Capacity:</strong> {team.capacity} points
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Velocity:</strong> {team.velocity} points/sprint
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Tab 2: Epics & Stories */}
        <TabPanel value={detailTabValue} index={1}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Program Epics - {epics.length} Epics
          </Typography>
          <TableContainer sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ width: '5%' }}></TableCell>
                  <TableCell sx={{ width: '10%' }}><strong>Epic ID</strong></TableCell>
                  <TableCell sx={{ width: '25%' }}><strong>Title</strong></TableCell>
                  <TableCell sx={{ width: '10%' }}><strong>Type</strong></TableCell>
                  <TableCell sx={{ width: '12%' }}><strong>Owner</strong></TableCell>
                  <TableCell sx={{ width: '10%' }}><strong>Priority</strong></TableCell>
                  <TableCell sx={{ width: '8%' }}><strong>WSJF</strong></TableCell>
                  <TableCell sx={{ width: '10%' }}><strong>Status</strong></TableCell>
                  <TableCell sx={{ width: '10%' }}><strong>Teams</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {epics.map((epic) => {
                  const epicStories = stories.filter(s => s.epicId === epic.epicId);
                  const isExpanded = expandedEpics.has(epic._id);
                  
                  return (
                    <React.Fragment key={epic._id}>
                      <TableRow 
                        hover
                        onClick={() => handleEpicRowClick(epic)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#f0f4f8' }
                        }}
                      >
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => toggleEpicExpansion(epic._id, e)}
                            aria-label={isExpanded ? 'Collapse stories' : 'Expand stories'}
                            sx={{ p: 0.5 }}
                          >
                            {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                          </IconButton>
                          <Chip 
                            label={epicStories.length} 
                            size="small" 
                            sx={{ ml: 0.5, height: 20, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {epic.epicId}
                          </Typography>
                        </TableCell>
                    <TableCell>
                      <Typography variant="body2">{epic.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {epic.description.substring(0, 80)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={epic.epicType} 
                        size="small" 
                        sx={{
                          ...getEpicTypeColor(epic.epicType),
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>{epic.owner}</TableCell>
                    <TableCell>
                      <Chip 
                        label={epic.priority} 
                        color={getPriorityColor(epic.priority) as any} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {epic.wsjfScore}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={epic.status} 
                        size="small" 
                        sx={{
                          ...getEpicStatusColor(epic.status),
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {epic.assignedTeams.map((team, idx) => {
                        const teamColor = getTeamColor(team);
                        return (
                          <Chip 
                            key={idx} 
                            label={team} 
                            size="small" 
                            sx={{ 
                              mr: 0.5, 
                              mb: 0.5,
                              bgcolor: teamColor.bg,
                              color: teamColor.text,
                              fontWeight: 600,
                              '&:hover': {
                                bgcolor: teamColor.bg,
                                opacity: 0.9
                              }
                            }} 
                          />
                        );
                      })}
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Stories for this Epic */}
                  {isExpanded && epicStories.map((story) => (
                    <TableRow 
                      key={story._id}
                      onClick={(e) => handleStoryRowClick(story, e)}
                      sx={{ 
                        bgcolor: '#f9fafb',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#e8eaf0' }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Click to edit ${story.storyId} - ${story.title}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleStoryRowClick(story, e as any);
                        }
                      }}
                    >
                      <TableCell></TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ pl: 2, color: '#666', fontSize: '0.85rem' }}>
                          {story.storyId}
                        </Typography>
                      </TableCell>
                      <TableCell colSpan={2}>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {story.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {story.assignedTeam}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={story.priority} 
                          size="small"
                          color={getPriorityColor(story.priority) as any}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {story.storyPoints} pts
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={story.status} 
                          size="small"
                          color={getStatusColor(story.status) as any}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          Sprint {story.sprint.sprintNumber}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            User Stories - {stories.length} Stories
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Story ID</strong></TableCell>
                  <TableCell><strong>Title</strong></TableCell>
                  <TableCell><strong>Epic</strong></TableCell>
                  <TableCell><strong>Sprint</strong></TableCell>
                  <TableCell><strong>Team</strong></TableCell>
                  <TableCell><strong>Points</strong></TableCell>
                  <TableCell><strong>Priority</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stories.map((story) => (
                  <TableRow key={story._id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {story.storyId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{story.title}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {story.epicId}
                      </Typography>
                    </TableCell>
                    <TableCell>Sprint {story.sprint.sprintNumber}</TableCell>
                    <TableCell>
                      {(() => {
                        const teamColor = getTeamColor(story.assignedTeam);
                        return (
                          <Chip 
                            label={story.assignedTeam} 
                            size="small" 
                            sx={{ 
                              bgcolor: teamColor.bg,
                              color: teamColor.text,
                              fontWeight: 600,
                              '&:hover': {
                                bgcolor: teamColor.bg,
                                opacity: 0.9
                              }
                            }} 
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Chip label={story.storyPoints} size="small" color="primary" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={story.priority} 
                        color={getPriorityColor(story.priority) as any} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={story.status} 
                        color={getStatusColor(story.status) as any} 
                        size="small" 
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab 3: ROAM Risks */}
        <TabPanel value={detailTabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              ROAM Risk Management - {selectedPIPlan.risks.length} Risks
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} size="small">
              Add Risk
            </Button>
          </Box>

          {/* ROAM Summary */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {['Resolved', 'Owned', 'Accepted', 'Mitigated'].map((category) => {
              const count = selectedPIPlan.risks.filter(r => r.roamCategory === category).length;
              return (
                <Grid item xs={6} md={3} key={category}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                        {count}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {category}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Risk ID</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>ROAM</strong></TableCell>
                  <TableCell><strong>Severity</strong></TableCell>
                  <TableCell><strong>Owner</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Mitigation Plan</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedPIPlan.risks.map((risk) => (
                  <TableRow key={risk.riskId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {risk.riskId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{risk.description}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={risk.roamCategory} 
                        color={getROAMColor(risk.roamCategory) as any} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={risk.severity} 
                        color={getPriorityColor(risk.severity) as any} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>{risk.owner}</TableCell>
                    <TableCell>
                      <Chip 
                        label={risk.status} 
                        color={getStatusColor(risk.status) as any} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {risk.mitigationPlan}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab 4: Roadmap & Backlog */}
        <TabPanel value={detailTabValue} index={3}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Program Objectives & Roadmap
          </Typography>
          
          <Grid container spacing={2}>
            {selectedPIPlan.objectives.map((objective) => (
              <Grid item xs={12} key={objective.objectiveId}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {objective.objectiveId}: {objective.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <Chip 
                            label={objective.priority} 
                            color={getPriorityColor(objective.priority) as any} 
                            size="small" 
                          />
                          <Chip 
                            label={objective.status} 
                            color={getStatusColor(objective.status) as any} 
                            size="small" 
                          />
                          {(() => {
                            const teamColor = getTeamColor(objective.assignedTeam);
                            return (
                              <Chip 
                                label={objective.assignedTeam} 
                                size="small" 
                                sx={{ 
                                  bgcolor: teamColor.bg,
                                  color: teamColor.text,
                                  fontWeight: 600,
                                  borderColor: teamColor.bg,
                                  '&:hover': {
                                    bgcolor: teamColor.bg,
                                    opacity: 0.9
                                  }
                                }} 
                              />
                            );
                          })()}
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary">
                          Business Value
                        </Typography>
                        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                          {objective.businessValue}/10
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Dependencies */}
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4 }}>
            Program Dependencies - {selectedPIPlan.dependencies.length} Dependencies
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>From Team</strong></TableCell>
                  <TableCell><strong>To Team</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedPIPlan.dependencies.map((dep) => (
                  <TableRow key={dep.dependencyId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {dep.dependencyId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const teamColor = getTeamColor(dep.fromTeam);
                        return (
                          <Chip 
                            label={dep.fromTeam} 
                            size="small" 
                            sx={{ 
                              bgcolor: teamColor.bg,
                              color: teamColor.text,
                              fontWeight: 600,
                              '&:hover': {
                                bgcolor: teamColor.bg,
                                opacity: 0.9
                              }
                            }} 
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const teamColor = getTeamColor(dep.toTeam);
                        return (
                          <Chip 
                            label={dep.toTeam} 
                            size="small" 
                            sx={{ 
                              bgcolor: teamColor.bg,
                              color: teamColor.text,
                              fontWeight: 600,
                              '&:hover': {
                                bgcolor: teamColor.bg,
                                opacity: 0.9
                              }
                            }} 
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>{dep.description}</TableCell>
                    <TableCell>
                      <Chip 
                        label={dep.status} 
                        color={getStatusColor(dep.status) as any} 
                        size="small" 
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab 5: RTE Role */}
        <TabPanel value={detailTabValue} index={4}>
          <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mb: 3 }}>
            Release Train Engineer (RTE) Role
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            The Release Train Engineer is a servant leader and chief facilitator of the Agile Release Train (ART), 
            responsible for aligning multiple Agile teams, supporting program execution, and ensuring teams continuously 
            deliver value to the organization.
          </Typography>

          {/* Core Responsibilities Grid */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* PI Planning Facilitation */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', border: '2px solid #003366' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      PI Planning Facilitation
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Organize and facilitate Program Increment Planning sessions every 8–12 weeks where all teams align on objectives.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Schedule logistics and prepare agenda</Typography></li>
                    <li><Typography variant="body2">Coordinate with Product Management</Typography></li>
                    <li><Typography variant="body2">Capture and manage dependencies and risks</Typography></li>
                    <li><Typography variant="body2">Ensure team commitment to unified plan</Typography></li>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* ART Execution Coordination */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <GroupsIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                      ART Execution Coordination
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Monitor progress across teams, manage cross-team dependencies, and maintain alignment.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Hold Scrum of Scrums meetings</Typography></li>
                    <li><Typography variant="body2">Track velocity and burn-down charts</Typography></li>
                    <li><Typography variant="body2">Measure PI progress and delivery health</Typography></li>
                    <li><Typography variant="body2">Coordinate System Demos</Typography></li>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Continuous Improvement */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUpIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                      Continuous Improvement
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Lead Inspect & Adapt workshops and root cause analyses to identify systemic inefficiencies.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Facilitate I&A workshops at PI end</Typography></li>
                    <li><Typography variant="body2">Conduct root cause problem-solving</Typography></li>
                    <li><Typography variant="body2">Track and analyze key metrics</Typography></li>
                    <li><Typography variant="body2">Promote iterative improvement</Typography></li>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Risk & Impediment Management */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <BlockIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                      Risk & Impediment Management
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Proactively remove blockers and implement risk mitigation strategies.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Identify and remove impediments</Typography></li>
                    <li><Typography variant="body2">Escalate major blockers</Typography></li>
                    <li><Typography variant="body2">Implement mitigation strategies</Typography></li>
                    <li><Typography variant="body2">Maintain program flow</Typography></li>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Stakeholder Communication */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ForumIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                      Stakeholder Communication
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Ensure transparent communication among all stakeholders to maintain alignment.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Bridge teams and leadership</Typography></li>
                    <li><Typography variant="body2">Align Product Owners and Scrum Masters</Typography></li>
                    <li><Typography variant="body2">Maintain consistent ART objectives</Typography></li>
                    <li><Typography variant="body2">Facilitate transparent reporting</Typography></li>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Servant Leadership & Coaching */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SchoolIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                      Servant Leadership & Coaching
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Mentor Scrum Masters and teams in Lean-Agile principles and foster team health.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Coach teams on SAFe principles</Typography></li>
                    <li><Typography variant="body2">Mentor Scrum Masters</Typography></li>
                    <li><Typography variant="body2">Foster collaboration and productivity</Typography></li>
                    <li><Typography variant="body2">Enhance team health and morale</Typography></li>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* ART Synchronization Section */}
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
            Agile Release Train (ART) Synchronization
          </Typography>
          
          <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
            <CardContent>
              <Typography variant="body1" paragraph>
                <strong>Synchronization</strong> means multiple Agile teams work in lockstep—planning, developing, 
                integrating, demonstrating, and improving together on the same fixed schedule (Program Increment). 
                This synchronized cadence lets teams deliver integrated, tested solutions at predictable intervals.
              </Typography>
            </CardContent>
          </Card>

          {/* Synchronization Mechanisms */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Program Increment Cadence
                  </Typography>
                  <Typography variant="body2">
                    All teams operate on a fixed 8–12 week PI schedule with synchronized timeboxed iterations (sprints) 
                    that start and end simultaneously, creating a reliable heartbeat for delivery.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <GroupsIcon sx={{ fontSize: 40, color: '#1976D2', mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Regular ART Sync Meetings
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>Scrum of Scrums:</strong> Daily/weekly coordination among Scrum Masters<br/>
                    <strong>ART Syncs:</strong> Weekly/biweekly progress review across teams<br/>
                    <strong>PO Syncs:</strong> Align Product Owners on priorities and dependencies
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <TrendingUpIcon sx={{ fontSize: 40, color: '#2E7D32', mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    System Demos & I&A
                  </Typography>
                  <Typography variant="body2">
                    Every iteration culminates in System Demos showcasing integrated increments. 
                    End-of-PI Inspect & Adapt workshops identify systemic issues and drive continuous improvement.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Why Synchronization Matters */}
          <Card sx={{ mt: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Why Synchronization Matters
              </Typography>
              <Typography variant="body1">
                By maintaining <strong>cadence</strong> (timing) and <strong>synchronization</strong> (alignment), 
                the ART fosters dependable delivery, early integration testing, fast feedback loops, and cross-team visibility. 
                Teams can self-correct quickly, ensuring consistent flow of value to customers and stakeholders.
              </Typography>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Tab 6: CBP Systems */}
        <TabPanel value={detailTabValue} index={5}>
          <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mb: 3 }}>
            DHS CBP IT Ecosystem (2025)
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            U.S. Customs and Border Protection operates one of the largest and most complex IT environments in the 
            federal government, with systems spanning border security, trade facilitation, immigration, and data analytics.
          </Typography>

          {/* Border Security Systems */}
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
            <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Border Security and Enforcement Systems
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '2px solid #D32F2F',
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}
                onClick={() => navigate('/admin/ats-system')}
                role="button"
                tabIndex={0}
                aria-label="View Automated Targeting System details"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/admin/ats-system');
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <RadarIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                      Automated Targeting System (ATS)
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Risk-assessment system for identifying high-risk travelers and shipments entering the U.S.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Traveler risk scoring and vetting</Typography></li>
                    <li><Typography variant="body2">Cargo and shipment threat analysis</Typography></li>
                    <li><Typography variant="body2">Real-time intelligence integration</Typography></li>
                    <li><Typography variant="body2">Multi-agency data correlation</Typography></li>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#D32F2F', fontWeight: 'bold', mt: 2, display: 'block' }}>
                    Click to view detailed system information →
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '2px solid #1976D2',
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}
                onClick={() => navigate('/admin/ift-rvss-system')}
                role="button"
                tabIndex={0}
                aria-label="View Integrated Fixed Tower and RVSS details"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/admin/ift-rvss-system');
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SecurityIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                      Integrated Fixed Tower (IFT) & RVSS
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    U.S. Border Patrol monitoring systems for detecting illegal crossings via sensor and camera integration.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Fixed tower surveillance network</Typography></li>
                    <li><Typography variant="body2">Remote video monitoring systems</Typography></li>
                    <li><Typography variant="body2">Sensor integration and alerts</Typography></li>
                    <li><Typography variant="body2">Real-time detection and response</Typography></li>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#1976D2', fontWeight: 'bold', mt: 2, display: 'block' }}>
                    Click to view detailed system information →
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '2px solid #7B1FA2',
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}
                onClick={() => navigate('/admin/eid-system')}
                role="button"
                tabIndex={0}
                aria-label="View Enforcement Integrated Database details"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/admin/eid-system');
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AssessmentIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                      Enforcement Integrated Database (EID)
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Central database supporting enforcement case tracking and biometric matching across DHS components.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Case management and tracking</Typography></li>
                    <li><Typography variant="body2">Biometric matching and verification</Typography></li>
                    <li><Typography variant="body2">Cross-agency data sharing</Typography></li>
                    <li><Typography variant="body2">Investigation support tools</Typography></li>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#7B1FA2', fontWeight: 'bold', mt: 2, display: 'block' }}>
                    Click to view detailed system information →
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '2px solid #2E7D32',
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}
                onClick={() => navigate('/admin/uip-system')}
                role="button"
                tabIndex={0}
                aria-label="View Unified Immigration Portal details"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/admin/uip-system');
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <GroupsIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                      Unified Immigration Portal (UIP)
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Provides CBP officers access to immigration and travel records through real-time integration.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">USCIS system integration</Typography></li>
                    <li><Typography variant="body2">ICE data connectivity</Typography></li>
                    <li><Typography variant="body2">Real-time record access</Typography></li>
                    <li><Typography variant="body2">Officer decision support</Typography></li>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 'bold', mt: 2, display: 'block' }}>
                    Click to view detailed system information →
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Trade and Cargo Systems */}
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
            <TradeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Trade and Cargo Management Systems
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '2px solid #003366',
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}
                onClick={() => navigate('/admin/ace-system')}
                role="button"
                tabIndex={0}
                aria-label="View Automated Commercial Environment details"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/admin/ace-system');
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TradeIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      Automated Commercial Environment (ACE)
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    The backbone of trade facilitation, used for imports, exports, and compliance processing.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Import/export processing</Typography></li>
                    <li><Typography variant="body2">Trade compliance management</Typography></li>
                    <li><Typography variant="body2">Duty and tariff calculation</Typography></li>
                    <li><Typography variant="body2">Partner Government Agency (PGA) integration</Typography></li>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold', mt: 2, display: 'block' }}>
                    Click to view detailed system information →
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '2px solid #F57C00',
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}
                onClick={() => navigate('/admin/abi-system')}
                role="button"
                tabIndex={0}
                aria-label="View Automated Broker Interface details"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/admin/abi-system');
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ComputerIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                      Automated Broker Interface (ABI)
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Communication portal between customs brokers and CBP for electronic filing of entries.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Electronic entry filing</Typography></li>
                    <li><Typography variant="body2">Broker-CBP communication</Typography></li>
                    <li><Typography variant="body2">Global Business Identifier (GBI) integration</Typography></li>
                    <li><Typography variant="body2">Real-time status updates</Typography></li>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#F57C00', fontWeight: 'bold', mt: 2, display: 'block' }}>
                    Click to view detailed system information →
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '2px solid #1976D2',
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}
                onClick={() => navigate('/admin/csms-system')}
                role="button"
                tabIndex={0}
                aria-label="View Cargo Systems Messaging Service details"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/admin/csms-system');
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ForumIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                      Cargo Systems Messaging Service (CSMS)
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Broadcasts operational updates and policy changes to importers, brokers, and carriers.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Policy change notifications</Typography></li>
                    <li><Typography variant="body2">Operational updates</Typography></li>
                    <li><Typography variant="body2">Trade community alerts</Typography></li>
                    <li><Typography variant="body2">System maintenance notices</Typography></li>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#1976D2', fontWeight: 'bold', mt: 2, display: 'block' }}>
                    Click to view detailed system information →
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Data, AI, and Analytics */}
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
            <AIIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Data, AI, and Analytics Platforms
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '2px solid #2E7D32',
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}
                onClick={() => navigate('/admin/aap-system')}
                role="button"
                tabIndex={0}
                aria-label="View Advanced Analytics Platform details"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/admin/aap-system');
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AssessmentIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                      CBP Data Ecosystem & Advanced Analytics Platform (AAP)
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Provides data aggregation, modeling, and predictive analytics for field operations and trade analysis.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Enterprise data aggregation</Typography></li>
                    <li><Typography variant="body2">Predictive modeling and analytics</Typography></li>
                    <li><Typography variant="body2">Field operations insights</Typography></li>
                    <li><Typography variant="body2">Trade pattern analysis</Typography></li>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 'bold', mt: 2, display: 'block' }}>
                    Click to view detailed system information →
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  height: '100%', 
                  border: '2px solid #7B1FA2',
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}
                onClick={() => navigate('/admin/ai-llm-system')}
                role="button"
                tabIndex={0}
                aria-label="View AI and LLM Initiatives details"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/admin/ai-llm-system');
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AIIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                      AI and LLM Initiatives (CBP AI Program Office)
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Developing generative AI pilots for risk detection, fentanyl interdiction, and operational efficiency.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Generative AI risk detection</Typography></li>
                    <li><Typography variant="body2">Fentanyl interdiction models</Typography></li>
                    <li><Typography variant="body2">Edge and cloud-based AI systems</Typography></li>
                    <li><Typography variant="body2">Operational efficiency automation</Typography></li>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#7B1FA2', fontWeight: 'bold', mt: 2, display: 'block' }}>
                    Click to view detailed system information →
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card 
                sx={{ 
                  border: '2px solid #1976D2',
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}
                onClick={() => navigate('/admin/cloud-edge-system')}
                role="button"
                tabIndex={0}
                aria-label="View Cloud and Edge Hybrid Infrastructure details"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/admin/cloud-edge-system');
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CloudIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                      Cloud and Edge Hybrid Infrastructure
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Dual approach allowing sensitive data processing at the border (edge) while maintaining enterprise systems in cloud environments.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Edge Computing:</Typography>
                      <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                        <li><Typography variant="body2">Border-based data processing</Typography></li>
                        <li><Typography variant="body2">Low-latency decision support</Typography></li>
                        <li><Typography variant="body2">Sensitive data isolation</Typography></li>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Cloud Infrastructure:</Typography>
                      <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                        <li><Typography variant="body2">Enterprise system hosting</Typography></li>
                        <li><Typography variant="body2">Scalable analytics platforms</Typography></li>
                        <li><Typography variant="body2">Centralized data management</Typography></li>
                      </Box>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" sx={{ color: '#1976D2', fontWeight: 'bold', mt: 2, display: 'block' }}>
                    Click to view detailed system information →
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Enterprise and Support Applications */}
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
            <ComputerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Enterprise and Support Applications
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  height: '100%',
                  border: '2px solid #1976D2',
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}
                onClick={() => navigate('/admin/oit-portal-system')}
                role="button"
                tabIndex={0}
                aria-label="View OIT Enterprise Service Portal details"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/admin/oit-portal-system');
                  }
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
                    OIT Enterprise Service Portal
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Used for service management, cybersecurity, and IT operations under the Chief Information Officer's purview.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">IT service management</Typography></li>
                    <li><Typography variant="body2">Cybersecurity operations</Typography></li>
                    <li><Typography variant="body2">Infrastructure monitoring</Typography></li>
                    <li><Typography variant="body2">Incident response coordination</Typography></li>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#1976D2', fontWeight: 'bold', mt: 2, display: 'block' }}>
                    Click to view detailed system information →
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  height: '100%',
                  border: '2px solid #F57C00',
                  cursor: 'pointer',
                  '&:hover': { 
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}
                onClick={() => navigate('/admin/innovation-hub-system')}
                role="button"
                tabIndex={0}
                aria-label="View CBP Innovation Hub details"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/admin/innovation-hub-system');
                  }
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#F57C00' }}>
                    CBP Innovation Hub
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Managed by the Office of the Commissioner to test and scale emerging technologies with mission offices.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li><Typography variant="body2">Emerging technology pilots</Typography></li>
                    <li><Typography variant="body2">Innovation testing and validation</Typography></li>
                    <li><Typography variant="body2">Mission office collaboration</Typography></li>
                    <li><Typography variant="body2">Technology scaling programs</Typography></li>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#F57C00', fontWeight: 'bold', mt: 2, display: 'block' }}>
                    Click to view detailed system information →
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Technology Stack & Architecture */}
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
            <ComputerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Technology Stack & Architecture (2025)
          </Typography>

          {/* Application Ownership */}
          <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Application Ownership Model
              </Typography>
              <Typography variant="body2" paragraph>
                CBP employs a hybrid model combining in-house development with commercial-off-the-shelf (COTS) applications, 
                integrated under a modern multi-cloud and DevSecOps architecture.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                    Custom-Built (CBP-Owned)
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                    <li><Typography variant="body2">ATS, UIP, EID (mission systems)</Typography></li>
                    <li><Typography variant="body2">Agency-owned codebases</Typography></li>
                    <li><Typography variant="body2">DHS-managed infrastructure</Typography></li>
                    <li><Typography variant="body2">Office of IT (OIT) maintained</Typography></li>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976D2', mb: 1 }}>
                    COTS/SaaS Integrations
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                    <li><Typography variant="body2">Salesforce (case management)</Typography></li>
                    <li><Typography variant="body2">ServiceNow (IT operations)</Typography></li>
                    <li><Typography variant="body2">Oracle ERP (enterprise)</Typography></li>
                    <li><Typography variant="body2">Configured, not code-owned</Typography></li>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#7B1FA2', mb: 1 }}>
                    Low-Code & Hybrid
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                    <li><Typography variant="body2">AI-driven rapid development</Typography></li>
                    <li><Typography variant="body2">Operational dashboards</Typography></li>
                    <li><Typography variant="body2">Cognitive border analysis</Typography></li>
                    <li><Typography variant="body2">DHS-approved cloud hosted</Typography></li>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Technology Stack */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                    Development Stack
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Frontend:</Typography>
                      <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                        <li><Typography variant="body2"><strong>React.js</strong> - Officer/analyst dashboards</Typography></li>
                        <li><Typography variant="body2"><strong>Angular</strong> - Enterprise applications</Typography></li>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Backend:</Typography>
                      <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                        <li><Typography variant="body2"><strong>Node.js</strong> - Microservices layer</Typography></li>
                        <li><Typography variant="body2"><strong>Java (Spring Boot)</strong> - Service framework</Typography></li>
                        <li><Typography variant="body2"><strong>.NET Core</strong> - Integration APIs</Typography></li>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Databases:</Typography>
                      <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                        <li><Typography variant="body2"><strong>PostgreSQL</strong> - Structured data</Typography></li>
                        <li><Typography variant="body2"><strong>Oracle Database</strong> - Enterprise data</Typography></li>
                        <li><Typography variant="body2"><strong>MongoDB</strong> - Document store</Typography></li>
                        <li><Typography variant="body2"><strong>Elasticsearch</strong> - Search & analytics</Typography></li>
                        <li><Typography variant="body2">~50% migrated to cloud by 2025</Typography></li>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                    Cloud & Infrastructure
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Multi-Cloud Environment:</Typography>
                      <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                        <li><Typography variant="body2"><strong>AWS GovCloud</strong> - Primary platform</Typography></li>
                        <li><Typography variant="body2"><strong>Microsoft Azure Government</strong></Typography></li>
                        <li><Typography variant="body2"><strong>Google Cloud Platform</strong></Typography></li>
                        <li><Typography variant="body2">Unified management interface</Typography></li>
                        <li><Typography variant="body2">Workload portability enabled</Typography></li>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Containerization:</Typography>
                      <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                        <li><Typography variant="body2"><strong>Docker</strong> - Container runtime</Typography></li>
                        <li><Typography variant="body2"><strong>Kubernetes</strong> - Orchestration</Typography></li>
                        <li><Typography variant="body2">Modular deployments</Typography></li>
                        <li><Typography variant="body2">Scalability across tiers</Typography></li>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* DevSecOps & Monitoring Tools */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                    Monitoring & Observability
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li>
                      <Typography variant="body2">
                        <strong>Splunk Enterprise</strong> - Log aggregation and SIEM
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>New Relic</strong> - Infrastructure monitoring and APM
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>Backstage</strong> - Service cataloging (pilot)
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Alerting across hybrid cloud assets
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Centralized observability dashboards
                      </Typography>
                    </li>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                    DevOps & Collaboration
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                    <li>
                      <Typography variant="body2">
                        <strong>JIRA & Confluence</strong> - Agile tracking and documentation
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>GitLab/GitHub Enterprise</strong> - Version control and CI/CD
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>Terraform</strong> - Infrastructure as Code
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>Ansible</strong> - Configuration management
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Sprint tracking and backlog management
                      </Typography>
                    </li>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ border: '2px solid #D32F2F' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                    Security & Compliance
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" paragraph>
                        Continuous monitoring aligned with <strong>DHS CDM (Continuous Diagnostics and Mitigation)</strong> standards.
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                        <li><Typography variant="body2"><strong>Tenable</strong> - Vulnerability scanning</Typography></li>
                        <li><Typography variant="body2"><strong>Qualys</strong> - Security assessment</Typography></li>
                        <li><Typography variant="body2">Internal DHS dashboards</Typography></li>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Compliance Framework:</Typography>
                      <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                        <li><Typography variant="body2">FedRAMP authorization</Typography></li>
                        <li><Typography variant="body2">FISMA compliance</Typography></li>
                        <li><Typography variant="body2">DHS security standards</Typography></li>
                        <li><Typography variant="body2">Automated vulnerability management</Typography></li>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Architecture Summary */}
          <Card sx={{ mb: 3, bgcolor: '#e8f5e9', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Architecture Summary
              </Typography>
              <Typography variant="body1" paragraph>
                CBP's technology environment blends <strong>agency-developed systems</strong> (mission-critical, custom-coded) 
                with <strong>commercial and open-source tools</strong> (DevSecOps and enterprise IT).
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Architecture:</Typography>
                  <Typography variant="body2">Multi-cloud, API-driven, microservice-oriented</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Development:</Typography>
                  <Typography variant="body2">React/Angular frontend, Java/Node.js backend</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Infrastructure:</Typography>
                  <Typography variant="body2">AWS, Azure, GCP with Docker/Kubernetes</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Operations:</Typography>
                  <Typography variant="body2">Splunk, New Relic, JIRA, GitLab</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* COTS vs Custom Applications */}
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
            <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            COTS vs Custom-Built Applications
          </Typography>

          <Card sx={{ mb: 3, bgcolor: '#fff3e0' }}>
            <CardContent>
              <Typography variant="body1" paragraph>
                CBP's portfolio is a blend of <strong>custom-built, government-owned systems</strong> for mission-critical operations 
                and <strong>commercial-off-the-shelf (COTS)</strong> platforms for enterprise IT, workflow, and observability.
              </Typography>
            </CardContent>
          </Card>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* COTS Applications */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                    Commercial Off-The-Shelf (COTS)
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ fontStyle: 'italic', mb: 2 }}>
                    Enterprise IT, workflow automation, and observability platforms
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      🔧 IT Service Management
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                      <li><Typography variant="body2"><strong>ServiceNow</strong> - IT service workflows, ticketing, automation</Typography></li>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      👥 CRM & Case Management
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                      <li><Typography variant="body2"><strong>Salesforce</strong> - Customer relationship management, case workflows</Typography></li>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      🏢 Enterprise Systems
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                      <li><Typography variant="body2"><strong>Oracle ERP</strong> - Enterprise resource planning</Typography></li>
                      <li><Typography variant="body2"><strong>Oracle Identity Management</strong> - User entitlement and access control</Typography></li>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      📋 Agile & Documentation
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                      <li><Typography variant="body2"><strong>JIRA</strong> - Agile project management, sprint tracking</Typography></li>
                      <li><Typography variant="body2"><strong>Confluence</strong> - Documentation and collaboration</Typography></li>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      📊 Monitoring & Observability
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                      <li><Typography variant="body2"><strong>Splunk</strong> - Log management and SIEM</Typography></li>
                      <li><Typography variant="body2"><strong>New Relic</strong> - IT observability and APM</Typography></li>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      🔓 Open-Source COTS
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                      <li><Typography variant="body2"><strong>Backstage</strong> - DevOps service cataloging (open-source)</Typography></li>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Custom-Built Applications */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', border: '2px solid #003366' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    Custom-Built (Government-Owned)
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ fontStyle: 'italic', mb: 2 }}>
                    Mission-critical border security, immigration, and data analytics systems
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      🎯 Border Security & Risk Management
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                      <li><Typography variant="body2"><strong>Automated Targeting System (ATS)</strong> - Mission-specific targeting and risk assessment, developed in-house</Typography></li>
                      <li><Typography variant="body2"><strong>Integrated Fixed Tower (IFT)</strong> - Border surveillance systems</Typography></li>
                      <li><Typography variant="body2"><strong>RVSS</strong> - Remote video surveillance</Typography></li>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      🛂 Immigration & Enforcement
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                      <li><Typography variant="body2"><strong>Unified Immigration Portal (UIP)</strong> - CBP OIT-built for immigration and border enforcement</Typography></li>
                      <li><Typography variant="body2"><strong>Enforcement Integrated Database (EID)</strong> - Central mission database for law enforcement case management</Typography></li>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      📦 Trade & Cargo
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                      <li><Typography variant="body2"><strong>Automated Commercial Environment (ACE)</strong> - Trade facilitation backbone</Typography></li>
                      <li><Typography variant="body2"><strong>Automated Broker Interface (ABI)</strong> - Broker communication system</Typography></li>
                      <li><Typography variant="body2"><strong>CSMS</strong> - Cargo systems messaging</Typography></li>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      🤖 AI & Advanced Analytics
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                      <li><Typography variant="body2"><strong>Advanced Analytics Platform (AAP)</strong> - Custom environment for CBP data modeling, AI development, and predictive analytics</Typography></li>
                      <li><Typography variant="body2"><strong>CBP AI Program Office Systems</strong> - Generative AI and LLM initiatives</Typography></li>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Application Summary Table */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Application Portfolio Summary
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Application</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Purpose</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>ServiceNow</TableCell>
                      <TableCell><Chip label="COTS" size="small" sx={{ bgcolor: '#F57C00', color: 'white' }} /></TableCell>
                      <TableCell>IT service workflows</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Salesforce</TableCell>
                      <TableCell><Chip label="COTS" size="small" sx={{ bgcolor: '#F57C00', color: 'white' }} /></TableCell>
                      <TableCell>CRM, case management</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Oracle ERP/IM Suite</TableCell>
                      <TableCell><Chip label="COTS" size="small" sx={{ bgcolor: '#F57C00', color: 'white' }} /></TableCell>
                      <TableCell>ERP, identity management</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>JIRA/Confluence</TableCell>
                      <TableCell><Chip label="COTS" size="small" sx={{ bgcolor: '#F57C00', color: 'white' }} /></TableCell>
                      <TableCell>Agile/Documentation</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Splunk, New Relic</TableCell>
                      <TableCell><Chip label="COTS" size="small" sx={{ bgcolor: '#F57C00', color: 'white' }} /></TableCell>
                      <TableCell>Monitoring & observability</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Backstage</TableCell>
                      <TableCell><Chip label="COTS (Open-Source)" size="small" sx={{ bgcolor: '#7B1FA2', color: 'white' }} /></TableCell>
                      <TableCell>DevOps catalog</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                      <TableCell>Automated Targeting System (ATS)</TableCell>
                      <TableCell><Chip label="Custom" size="small" sx={{ bgcolor: 'primary.main', color: 'white' }} /></TableCell>
                      <TableCell>Border security & risk</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                      <TableCell>Unified Immigration Portal (UIP)</TableCell>
                      <TableCell><Chip label="Custom" size="small" sx={{ bgcolor: 'primary.main', color: 'white' }} /></TableCell>
                      <TableCell>CBP/DHS owned immigration system</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                      <TableCell>Enforcement Integrated Database (EID)</TableCell>
                      <TableCell><Chip label="Custom" size="small" sx={{ bgcolor: 'primary.main', color: 'white' }} /></TableCell>
                      <TableCell>Mission database</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                      <TableCell>Advanced Analytics Platform (AAP)</TableCell>
                      <TableCell><Chip label="Custom" size="small" sx={{ bgcolor: 'primary.main', color: 'white' }} /></TableCell>
                      <TableCell>AI/data modeling</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                      <TableCell>ACE, ABI, CSMS</TableCell>
                      <TableCell><Chip label="Custom" size="small" sx={{ bgcolor: 'primary.main', color: 'white' }} /></TableCell>
                      <TableCell>Trade & cargo systems</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Key Insight */}
          <Card sx={{ mb: 3, bgcolor: '#e8f5e9', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Strategic Application Model
              </Typography>
              <Typography variant="body1">
                CBP relies on <strong>COTS platforms</strong> for enterprise IT, workflow automation, and observability, 
                while <strong>mission applications</strong> (border security, immigration, trade, data modeling) are predominantly 
                <strong> custom-built and government-owned</strong> to meet specific operational requirements and security standards.
              </Typography>
            </CardContent>
          </Card>

          {/* Summary Card */}
          <Card sx={{ mt: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                CBP Tech Ecosystem Summary (2025)
              </Typography>
              <Typography variant="body1" paragraph>
                CBP's core technology ecosystem revolves around <strong>ACE</strong> and <strong>ATS</strong> for trade and risk management, 
                along with a growing <strong>AI-enabled data environment</strong> integrating cloud, edge, and analytics platforms to enhance 
                national security and operational awareness.
              </Typography>
              <Typography variant="body2">
                <strong>Key Focus Areas:</strong> Border security enforcement, trade facilitation, AI-driven analytics, 
                cloud/edge hybrid infrastructure, and enterprise IT modernization.
              </Typography>
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>

      {/* Epic Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        aria-labelledby="epic-edit-dialog-title"
      >
        <DialogTitle id="epic-edit-dialog-title" sx={{ bgcolor: 'primary.main', color: 'white' }}>
          Edit Epic: {selectedEpic?.epicId}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            {/* Title - Full Width */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={epicFormData.title || ''}
                onChange={(e) => handleFormChange('title', e.target.value)}
                variant="outlined"
                required
                aria-label="Epic title"
                helperText="Epic title"
              />
            </Grid>

            {/* Description - Full Width */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={epicFormData.description || ''}
                onChange={(e) => handleFormChange('description', e.target.value)}
                variant="outlined"
                multiline
                rows={4}
                required
                aria-label="Epic description"
                helperText="Detailed epic description"
              />
            </Grid>

            {/* Row 1: Epic Type, Owner, Status */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Epic Type</InputLabel>
                <Select
                  value={epicFormData.epicType || ''}
                  onChange={(e) => handleFormChange('epicType', e.target.value)}
                  label="Epic Type"
                  aria-label="Epic type"
                >
                  <MenuItem value="Business">Business</MenuItem>
                  <MenuItem value="Enabler">Enabler</MenuItem>
                  <MenuItem value="Architectural">Architectural</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Owner"
                value={epicFormData.owner || ''}
                onChange={(e) => handleFormChange('owner', e.target.value)}
                variant="outlined"
                required
                aria-label="Epic owner"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={epicFormData.status || ''}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  label="Status"
                  aria-label="Epic status"
                >
                  <MenuItem value="Backlog">Backlog</MenuItem>
                  <MenuItem value="Analyzing">Analyzing</MenuItem>
                  <MenuItem value="Portfolio Review">Portfolio Review</MenuItem>
                  <MenuItem value="Implementing">Implementing</MenuItem>
                  <MenuItem value="Done">Done</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Row 2: Priority and Estimated Effort */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={epicFormData.priority || ''}
                  onChange={(e) => handleFormChange('priority', e.target.value)}
                  label="Priority"
                  aria-label="Epic priority"
                >
                  <MenuItem value="Critical">Critical</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Estimated Effort</InputLabel>
                <Select
                  value={epicFormData.estimatedEffort || ''}
                  onChange={(e) => handleFormChange('estimatedEffort', e.target.value)}
                  label="Estimated Effort"
                  aria-label="Estimated effort"
                >
                  <MenuItem value="XS">XS</MenuItem>
                  <MenuItem value="S">S</MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="L">L</MenuItem>
                  <MenuItem value="XL">XL</MenuItem>
                  <MenuItem value="XXL">XXL</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Row 3: WSJF Components - Business Value, Time Criticality, Risk Reduction, WSJF Score */}
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Business Value (1-10)"
                type="number"
                value={epicFormData.businessValue || ''}
                onChange={(e) => handleFormChange('businessValue', Number(e.target.value))}
                variant="outlined"
                inputProps={{ min: 1, max: 10 }}
                aria-label="Business value"
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Time Criticality (1-10)"
                type="number"
                value={epicFormData.timeCriticality || ''}
                onChange={(e) => handleFormChange('timeCriticality', Number(e.target.value))}
                variant="outlined"
                inputProps={{ min: 1, max: 10 }}
                aria-label="Time criticality"
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Risk Reduction (1-10)"
                type="number"
                value={epicFormData.riskReduction || ''}
                onChange={(e) => handleFormChange('riskReduction', Number(e.target.value))}
                variant="outlined"
                inputProps={{ min: 1, max: 10 }}
                aria-label="Risk reduction"
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="WSJF Score"
                type="number"
                value={epicFormData.wsjfScore || ''}
                onChange={(e) => handleFormChange('wsjfScore', Number(e.target.value))}
                variant="outlined"
                aria-label="WSJF score"
                helperText="Auto-calculated"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ color: '#666' }}
            aria-label="Cancel editing"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEpic}
            variant="contained"
            sx={{ 
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
            aria-label="Save epic changes"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Story Edit Dialog */}
      <Dialog 
        open={storyDialogOpen} 
        onClose={handleCloseStoryDialog}
        maxWidth="md"
        fullWidth
        aria-labelledby="story-edit-dialog-title"
      >
        <DialogTitle id="story-edit-dialog-title" sx={{ bgcolor: 'primary.main', color: 'white' }}>
          Edit Story: {selectedStory?.storyId}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            {/* Title - Full Width */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={storyFormData.title || ''}
                onChange={(e) => handleStoryFormChange('title', e.target.value)}
                variant="outlined"
                required
                aria-label="Story title"
              />
            </Grid>

            {/* Description - Full Width */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={storyFormData.description || ''}
                onChange={(e) => handleStoryFormChange('description', e.target.value)}
                variant="outlined"
                multiline
                rows={3}
                required
                aria-label="Story description"
              />
            </Grid>

            {/* Row 1: Assigned Team, Status, Priority */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Assigned Team"
                value={storyFormData.assignedTeam || ''}
                onChange={(e) => handleStoryFormChange('assignedTeam', e.target.value)}
                variant="outlined"
                required
                aria-label="Assigned team"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={storyFormData.status || ''}
                  onChange={(e) => handleStoryFormChange('status', e.target.value)}
                  label="Status"
                  aria-label="Story status"
                >
                  <MenuItem value="Backlog">Backlog</MenuItem>
                  <MenuItem value="Ready">Ready</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="In Review">In Review</MenuItem>
                  <MenuItem value="Testing">Testing</MenuItem>
                  <MenuItem value="Done">Done</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={storyFormData.priority || ''}
                  onChange={(e) => handleStoryFormChange('priority', e.target.value)}
                  label="Priority"
                  aria-label="Story priority"
                >
                  <MenuItem value="Critical">Critical</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Row 2: Story Points */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Story Points</InputLabel>
                <Select
                  value={storyFormData.storyPoints || ''}
                  onChange={(e) => handleStoryFormChange('storyPoints', Number(e.target.value))}
                  label="Story Points"
                  aria-label="Story points"
                >
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={8}>8</MenuItem>
                  <MenuItem value={13}>13</MenuItem>
                  <MenuItem value={21}>21</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Acceptance Criteria - Full Width */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Acceptance Criteria (comma-separated)"
                value={Array.isArray(storyFormData.acceptanceCriteria) ? storyFormData.acceptanceCriteria.join(', ') : ''}
                onChange={(e) => handleStoryFormChange('acceptanceCriteria', e.target.value.split(',').map(s => s.trim()))}
                variant="outlined"
                multiline
                rows={2}
                aria-label="Acceptance criteria"
                helperText="Enter criteria separated by commas"
              />
            </Grid>

            {/* Tags - Full Width */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags (comma-separated)"
                value={Array.isArray(storyFormData.tags) ? storyFormData.tags.join(', ') : ''}
                onChange={(e) => handleStoryFormChange('tags', e.target.value.split(',').map(s => s.trim()))}
                variant="outlined"
                aria-label="Tags"
                helperText="Enter tags separated by commas"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button 
            onClick={handleCloseStoryDialog}
            sx={{ color: '#666' }}
            aria-label="Cancel editing"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveStory}
            variant="contained"
            sx={{ 
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
            aria-label="Save story changes"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PIPlanningBoard;
